/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { css } from "@emotion/css";
import type { PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { Callout, RadioButton, RadioButtonGroup, Select } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { EnvironmentV2Resource, PermissionDescriptions, Permissions, ProjectedTeamReferenceDataItem, ProjectGroupResource, ProjectSummaryResource, Repository, SpaceResource, TenantResource, UserResource, } from "@octopusdeploy/octopus-server-client";
import { Permission, getStaticAndParentEnvironmentsDependingOnIfEphemeralEnvironmentsAreEnabled } from "@octopusdeploy/octopus-server-client";
import type { QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import { keyBy } from "lodash";
import { useCallback } from "react";
import { useEphemeralEnvironmentsEnabled } from "~/areas/projects/components/EphemeralEnvironments/useEphemeralEnvironmentsEnabled";
import InternalLink from "~/components/Navigation/InternalLink/InternalLink";
import { PageContent } from "~/components/PageContent/PageContent";
import { SimplePagingList } from "~/components/PagingList";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { Section } from "~/components/Section/Section";
import type { AsyncListConfig } from "~/hooks/useAsyncList";
import { createAsyncList, useAsyncList } from "~/hooks/useAsyncList";
import ProjectedTeamsList, { ProjectedTeamListItem } from "../ProjectedTeamsList/ProjectedTeamsList";
import { PermissionTable } from "./PermissionTable";
export interface TestPermissionsPageProps {
    loaderData: TestPermissionsPageLoaderData;
    queryParams: TestPermissionsPageQueryParams;
    setQueryParams: QueryParamValuesSetter<TestPermissionsPageQueryParams>;
}
export interface TestPermissionsPageQueryParams {
    userId: string | undefined;
    spaceId: string | undefined;
    permissionScope: PermissionScope;
}
interface TestPermissionsPageLoaderData {
    usersAsyncListConfig: AsyncListConfig<UserResource>;
    permissionDescriptions: PermissionDescriptions;
}
interface UserPermissions {
    projectedTeamReferenceDataItems: ProjectedTeamReferenceDataItem[];
    spacePermissions: Permissions;
    systemPermissions: PermissionListItem[];
    isTeamsComplete: boolean;
    isPermissionsComplete: boolean;
    projectSummaries: {
        [ID: string]: ProjectSummaryResource;
    };
    environments: {
        [ID: string]: EnvironmentV2Resource;
    };
    tenants: {
        [ID: string]: TenantResource;
    };
    projectGroups: {
        [ID: string]: ProjectGroupResource;
    };
}
interface PermissionListItem {
    Id: string;
    Name: string;
}
const permissionScopeValues = ["System", "Space"] as const;
type PermissionScope = (typeof permissionScopeValues)[number];
class SystemPermissionList extends SimplePagingList<PermissionListItem> {
}
export async function testPermissionsPageLoader(repository: Repository, queryParams: TestPermissionsPageQueryParams): Promise<TestPermissionsPageLoaderData> {
    const selectedUser = queryParams.userId ? await repository.Users.get(queryParams.userId) : undefined;
    const permissionDescriptionsAsync = repository.PermissionDescriptions.all();
    const usersAsyncListConfig = createAsyncList(repository, {
        loadItems: async (repository, take, filter) => getUsers(repository, take, filter),
        name: "Users",
        getItemId: (item) => item.Id,
        initialItems: selectedUser ? [selectedUser] : [],
    });
    return {
        usersAsyncListConfig,
        permissionDescriptions: await permissionDescriptionsAsync,
    };
}
async function getUsers(repository: Repository, take: number, filter?: string) {
    const users = await repository.Users.list({ take, filter });
    return users.Items;
}
export function TestPermissionsPage({ loaderData, queryParams, setQueryParams }: TestPermissionsPageProps) {
    const usersAsyncList = useAsyncList(loaderData.usersAsyncListConfig);
    const selectedUserId = queryParams.userId;
    const selectedUser = selectedUserId ? usersAsyncList.getItemById(selectedUserId) : undefined;
    const { result } = useQuery(async (repository) => {
        if (!selectedUser)
            return null;
        return getAccessibleSpacesAndExportLink(repository, selectedUser, queryParams.spaceId);
    }, [queryParams.spaceId, selectedUser], "Get accessible spaces and permission export link for selected user");
    const exportPageAction: PrimaryPageAction | undefined = result
        ? {
            type: "navigate",
            label: "Export user permissions",
            external: true,
            path: result.exportUrl,
        }
        : undefined;
    const onUserChange = useCallback((userId: string | undefined) => {
        setQueryParams({ ...queryParams, userId, spaceId: undefined });
    }, [queryParams, setQueryParams]);
    const onSpaceChange = useCallback((spaceId: string | undefined) => {
        setQueryParams({ ...queryParams, spaceId });
    }, [queryParams, setQueryParams]);
    const onPermissionScopeChange = useCallback((permissionScope: PermissionScope) => {
        setQueryParams({ ...queryParams, permissionScope, spaceId: undefined });
    }, [queryParams, setQueryParams]);
    return (<PageContent header={{ title: "Test Permissions", primaryAction: exportPageAction }}>
            <Section>
                <Select label="Select a user" allowFilter={true} autoFocus={true} items={usersAsyncList} getOption={(u) => ({ value: u.Id, label: `${u.Username} (${u.DisplayName})` })} onChange={onUserChange} value={selectedUserId}/>
            </Section>
            {selectedUser && (<Section sectionHeader="Permissions context">
                    <RadioButtonGroup accessibleName={"Permissions Context"} value={queryParams.permissionScope} onChange={onPermissionScopeChange}>
                        <RadioButton value={"System"} label="Show system permissions"/>
                        <RadioButton value={"Space"} label="Show permissions within a specific space"/>
                    </RadioButtonGroup>
                </Section>)}
            {selectedUser && result && queryParams.permissionScope === "Space" && (<UserSpacePermissions user={selectedUser} spaceId={queryParams.spaceId} onSpaceIdChange={onSpaceChange} accessibleSpaces={result.accessibleSpaces} permissionDescriptions={loaderData.permissionDescriptions}/>)}
            {selectedUser && result && queryParams.permissionScope === "System" && <UserSystemPermissions user={selectedUser} accessibleSpaces={result.accessibleSpaces}/>}
        </PageContent>);
}
function UserSystemPermissions({ user, accessibleSpaces }: {
    user: UserResource;
    accessibleSpaces: SpaceResource[];
}) {
    const { result } = useQuery(async (repository) => getSystemPermissions(repository, user), [user], "Get system permissions for selected user");
    if (!result)
        return null;
    return (<>
            <TeamPermissionRenderer user={user} isTeamsComplete={result.isTeamsComplete} projectedTeamReferenceDataItems={result.projectedTeamReferenceDataItems} spacesAccessibleBySelectedUser={accessibleSpaces}/>
            <Section sectionHeader={result.systemPermissions.length === 0 ? "No permissions" : "Permissions"}>
                {!result.isPermissionsComplete && <IncompletePermissionsInfoCallout displayName={user.DisplayName}/>}
                {result.systemPermissions.length > 0 && <SystemPermissionList items={result.systemPermissions} onRow={(p: PermissionListItem) => <div>{p.Name}</div>}/>}
            </Section>
        </>);
}
function UserSpacePermissions({ user, spaceId, onSpaceIdChange, accessibleSpaces, permissionDescriptions, }: {
    user: UserResource;
    spaceId: string | undefined;
    onSpaceIdChange: (spaceId: string | undefined) => void;
    accessibleSpaces: SpaceResource[];
    permissionDescriptions: PermissionDescriptions;
}) {
    const ephemeralEnvironmentsAreEnabled = useEphemeralEnvironmentsEnabled();
    const { result } = useQuery(async (repository) => {
        if (!spaceId)
            return null;
        return getUserPermissionForSpace(repository, spaceId, user, ephemeralEnvironmentsAreEnabled);
    }, [spaceId, user, ephemeralEnvironmentsAreEnabled], "Get space permissions for selected user");
    return (<>
            <div className={styles.userSelect}>
                <Select label="Select a space" allowFilter={true} autoFocus={true} items={accessibleSpaces} getOption={(s) => ({ value: s.Id, label: `${s.Name} Space` })} onChange={onSpaceIdChange} value={spaceId}/>
            </div>
            {result && (<>
                    <TeamPermissionRenderer user={user} isTeamsComplete={result.isTeamsComplete} projectedTeamReferenceDataItems={result.projectedTeamReferenceDataItems} spacesAccessibleBySelectedUser={accessibleSpaces}/>
                    <Section sectionHeader={Object.keys(result.spacePermissions).length === 0 ? "No permissions" : "Permissions"}>
                        {!result.isPermissionsComplete && <IncompletePermissionsInfoCallout displayName={user.DisplayName}/>}
                        <p>
                            <InternalLink to={links.userPage.generateUrl({ userId: user.Id })}>{user.Username || user.DisplayName}'s </InternalLink>
                            permissions are restricted to:
                        </p>
                        <PermissionTable showRestrictions={true} permissions={result.spacePermissions} permissionDescriptions={permissionDescriptions} projectSummaries={result.projectSummaries!} environments={result.environments!} tenants={result.tenants!} projectGroups={result.projectGroups!}/>
                    </Section>
                </>)}
        </>);
}
function TeamPermissionRenderer({ user, isTeamsComplete, projectedTeamReferenceDataItems, spacesAccessibleBySelectedUser, }: {
    user: UserResource;
    isTeamsComplete: boolean;
    projectedTeamReferenceDataItems: ProjectedTeamReferenceDataItem[];
    spacesAccessibleBySelectedUser?: SpaceResource[];
}) {
    return (<Section sectionHeader="Teams">
            The user <InternalLink to={links.userPage.generateUrl({ userId: user.Id })}>{user.Username} </InternalLink>
            is a member of the following teams:
            {!isTeamsComplete && (<Callout type={"information"} title={"Limited permissions"}>
                    Your permissions do not allow you to see all of the teams that '{user.DisplayName}' is a member of
                </Callout>)}
            <ProjectedTeamsList items={projectedTeamReferenceDataItems} onRowRedirectUrl={(teamReference) => links.teamPage.generateUrl({ teamId: teamReference.Id })} onRow={(team) => <ProjectedTeamListItem projectedTeam={team} spaceResource={spacesAccessibleBySelectedUser?.find((x) => team.SpaceId && x.Id === team.SpaceId)}/>}/>
        </Section>);
}
function IncompletePermissionsInfoCallout({ displayName }: {
    displayName: string;
}) {
    return (<Callout type={"information"} title={"Limited permissions"}>
            Your permissions do not allow you to see all of the permissions granted to '{displayName}'
        </Callout>);
}
async function getAccessibleSpacesAndExportLink(repository: Repository, user: UserResource, spaceId: string | undefined) {
    const accessibleSpaces = await repository.Users.getSpaces(user);
    const systemLevelRepository = repository.forSystem();
    const userPermissions = await systemLevelRepository.UserPermissions.getPermissionsConfigurationForAllParitions(user, true);
    const exportUrl = spaceId ? repository.resolve(userPermissions.Links["Export"], { spaces: spaceId }) : repository.resolve(userPermissions.Links["Export"]);
    return {
        accessibleSpaces,
        exportUrl,
    };
}
async function getSystemPermissions(repository: Repository, user: UserResource): Promise<UserPermissions> {
    const systemLevelRepository = repository.forSystem();
    const userPermissions = await systemLevelRepository.UserPermissions.getPermissionsConfigurationForCurrentSpaceContext(user, true);
    return {
        projectedTeamReferenceDataItems: userPermissions.Teams,
        spacePermissions: userPermissions.SpacePermissions,
        systemPermissions: userPermissions.SystemPermissions.map((p: Permission) => ({ Id: p, Name: p })),
        isTeamsComplete: userPermissions.IsTeamsComplete,
        isPermissionsComplete: userPermissions.IsPermissionsComplete,
        projectSummaries: {},
        environments: {},
        projectGroups: {},
        tenants: {},
    };
}
async function getUserPermissionForSpace(repository: Repository, spaceId: string, selectedUser: UserResource, ephemeralEnvironmentsAreEnabled: boolean): Promise<UserPermissions> {
    const spaceLevelRepository = await repository.forSpace(spaceId);
    const userPermissions = await spaceLevelRepository.UserPermissions.getPermissionsConfigurationForCurrentSpaceContext(selectedUser, false);
    const tenants = isAllowed({ permission: Permission.TenantView }, { spaceId }) && isAllowed({ permission: Permission.TenantView, tenant: "*" }) ? spaceLevelRepository.Tenants.all() : Promise.resolve([]);
    const projectSummaries = isAllowed({ permission: Permission.ProjectView }, { spaceId }) ? spaceLevelRepository.Projects.summaries() : Promise.resolve([]);
    const environments = isAllowed({ permission: Permission.EnvironmentView }, { spaceId }) ? getStaticAndParentEnvironmentsDependingOnIfEphemeralEnvironmentsAreEnabled(ephemeralEnvironmentsAreEnabled, spaceLevelRepository) : Promise.resolve([]);
    const projectGroups = isAllowed({ permission: Permission.ProjectGroupView }, { spaceId }) ? spaceLevelRepository.ProjectGroups.all() : Promise.resolve([]);
    return {
        projectedTeamReferenceDataItems: userPermissions.Teams,
        spacePermissions: userPermissions.SpacePermissions,
        systemPermissions: userPermissions.SystemPermissions.map((p) => ({ Id: p, Name: p })),
        isTeamsComplete: userPermissions.IsTeamsComplete,
        isPermissionsComplete: userPermissions.IsPermissionsComplete,
        projectSummaries: keyBy(await projectSummaries, "Id"),
        environments: keyBy(await environments, "Id"),
        tenants: keyBy(await tenants, "Id"),
        projectGroups: keyBy(await projectGroups, "Id"),
    };
}
const styles = {
    userSelect: css({
        margin: `0 ${space[16]}`,
    }),
};
